/**
 * FurniturePage — Healthcare Examination Furniture marketplace.
 *
 * Mirrors 21 SKUs previously listed on sterilemate.com under the same
 * category name.  Every image is served from our /api/static mirror so
 * we're never dependent on the WordPress site.
 *
 * There is no public price on this line — instead each card has a
 * "Request a Quote" button that opens an in-page modal posting to
 * /api/quote-requests.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import MarketplaceCategoryNav from "@/components/MarketplaceCategoryNav";
import MarketplaceBreadcrumb from "@/components/MarketplaceBreadcrumb";
import { API } from "@/App";
import { FURNITURE_PRODUCTS, FURNITURE_CATEGORIES } from "@/data/furnitureCatalog";
import { ArmchairIcon, Mail, Phone, Filter, ClipboardList, FileText } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function FurniturePage() {
  const [cat, setCat] = useState("all");
  const [quoteProduct, setQuoteProduct] = useState(null);
  const [specsSlug, setSpecsSlug] = useState(null);
  const [specsIndex, setSpecsIndex] = useState({});

  // One-shot fetch of the specs index so we know which products have
  // detailed specs available (all 21 today, but the flag lets us dark-ship
  // future SKUs without a redeploy).
  useEffect(() => {
    axios.get(`${API}/furniture/specs`)
      .then((r) => setSpecsIndex(r.data.products || {}))
      .catch(() => {});
  }, []);

  const products = useMemo(() => {
    if (cat === "all") return FURNITURE_PRODUCTS;
    return FURNITURE_PRODUCTS.filter((p) => p.category === cat);
  }, [cat]);

  const counts = useMemo(() => {
    const c = {};
    for (const p of FURNITURE_PRODUCTS) c[p.category] = (c[p.category] || 0) + 1;
    c.all = FURNITURE_PRODUCTS.length;
    return c;
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <MarketplaceCategoryNav />
      <MarketplaceBreadcrumb items={[{ label: "Marketplace", to: "/marketplace" }, { label: "Healthcare Examination Furniture" }]} />

      {/* HERO */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <ArmchairIcon className="w-7 h-7" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[2.5px] text-blue-200 mb-2">
                Marketplace · Clinical Capital Equipment
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight max-w-3xl">
                Healthcare Examination Furniture
              </h1>
              <p className="mt-4 text-slate-200 max-w-2xl text-base leading-relaxed">
                Brewer, Ritter and Millennium Series exam tables, dental chairs, treatment tables
                and clinical stools. Same-day quotes with financing options. Certified Small Business.
              </p>
              <div className="mt-6 flex flex-wrap gap-2 text-xs text-blue-100">
                <span className="px-3 py-1.5 bg-white/10 rounded-full">21 configured models</span>
                <span className="px-3 py-1.5 bg-white/10 rounded-full">3-year manufacturer warranty</span>
                <span className="px-3 py-1.5 bg-white/10 rounded-full">GSA schedule pricing available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="border-b border-slate-200 bg-white sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center gap-2 overflow-x-auto no-scrollbar">
          <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-[10px] font-black uppercase tracking-[1.5px] text-slate-500 shrink-0 mr-1">Filter:</span>
          {FURNITURE_CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCat(c.key)}
              data-testid={`furniture-filter-${c.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold border transition-colors shrink-0 ${
                cat === c.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:border-blue-500 hover:text-blue-700"
              }`}
            >
              {c.label}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-4 px-1 rounded text-[10px] font-black ${
                cat === c.key ? "bg-white text-slate-900" : "bg-slate-100 text-slate-600"
              }`}>{counts[c.key] || 0}</span>
            </button>
          ))}
        </div>
      </section>

      {/* GRID */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`furniture-card-${p.id}`}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-lg hover:border-blue-300 transition-all"
            >
              <div className="aspect-square bg-slate-50 relative overflow-hidden border-b border-slate-100">
                <img
                  src={`${BACKEND}${p.image}`}
                  alt={p.name}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-contain p-4"
                />
                <span className="absolute top-2 left-2 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-white/90 text-slate-700 border border-slate-200">
                  {FURNITURE_CATEGORIES.find((c) => c.key === p.category)?.label.replace(" & ", "/") || p.category}
                </span>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="font-black text-slate-900 text-sm leading-tight">{p.name}</h3>
                <p className="text-xs text-slate-500 mt-1.5 flex-1">{p.tagline}</p>
                <div className="mt-3 flex items-stretch gap-2">
                  {specsIndex[p.id] && (
                    <button
                      type="button"
                      onClick={() => setSpecsSlug(p.id)}
                      title="View full specifications & spec sheet"
                      data-testid={`furniture-specs-${p.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border-2 border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 text-xs font-bold transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Specs
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setQuoteProduct(p)}
                    data-testid={`furniture-quote-${p.id}`}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors"
                  >
                    <ClipboardList className="w-3.5 h-3.5" />
                    Request a Quote
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA — batch quote */}
      <section className="bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-200">Outfitting a new clinic?</p>
            <h2 className="text-2xl font-black mt-1">Request a full multi-unit quote — one call, one PO.</h2>
          </div>
          <div className="flex gap-3">
            <a
              href="mailto:orders@sterilemate.com?subject=Furniture%20Multi-Unit%20Quote"
              data-testid="furniture-cta-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-white text-blue-900 text-sm font-black hover:bg-blue-50"
            >
              <Mail className="w-4 h-4" /> Email orders@sterilemate.com
            </a>
            <a
              href="tel:+18009883892"
              data-testid="furniture-cta-phone"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-blue-800 text-white text-sm font-black hover:bg-blue-700 border border-blue-700"
            >
              <Phone className="w-4 h-4" /> (800) 988-3892
            </a>
          </div>
        </div>
      </section>

      {quoteProduct && (
        <FurnitureQuoteModal
          product={quoteProduct}
          onClose={() => setQuoteProduct(null)}
        />
      )}
      {specsSlug && (
        <FurnitureSpecsModal
          slug={specsSlug}
          onOpenQuote={() => {
            const p = FURNITURE_PRODUCTS.find((x) => x.id === specsSlug);
            setSpecsSlug(null);
            setQuoteProduct(p);
          }}
          onClose={() => setSpecsSlug(null)}
        />
      )}
    </div>
  );
}

function FurnitureSpecsModal({ slug, onClose, onOpenQuote }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("description");
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/furniture/specs/${slug}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) setError(e.response?.status || "err"); });
    return () => { cancel = true; };
  }, [slug]);

  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const tabs = [
    { id: "description", label: "Description" },
    { id: "specs",       label: "Specifications" },
    { id: "downloads",   label: "Spec Sheet" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="furniture-specs-modal"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">Full Specifications</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">{data?.title || slug}</h3>
          </div>
          <button onClick={onClose} data-testid="furniture-specs-close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-2xl text-slate-500">×</button>
        </header>
        {!data && !error && (
          <div className="flex-1 flex items-center justify-center text-slate-500 py-12">Loading specifications…</div>
        )}
        {error && (
          <div className="flex-1 p-8 text-center text-slate-500">Specs not yet on file. Please request a quote and we&apos;ll send full details.</div>
        )}
        {data && (
          <>
            <nav className="px-6 flex gap-1 border-b bg-slate-50">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  data-testid={`furniture-specs-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 text-xs font-bold border-b-2 transition-colors ${
                    tab === t.id
                      ? "border-blue-600 text-blue-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >{t.label}</button>
              ))}
            </nav>
            <div className="flex-1 overflow-y-auto p-6">
              {tab === "description" && (
                <div className="prose prose-slate max-w-none">
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {data.description || "—"}
                  </p>
                  {data.features?.length > 0 && (
                    <div className="mt-5">
                      <p className="text-xs font-black uppercase text-slate-500 mb-2">Features</p>
                      <ul className="space-y-1">
                        {data.features.map((f, i) => (
                          <li key={i} className="text-sm text-slate-700 flex gap-2">
                            <span className="text-blue-600">•</span><span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {tab === "specs" && (
                <div>
                  {data.spec_matrix && data.spec_matrix.rows?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            {data.spec_matrix.headers.map((h, i) => (
                              <th key={i} className="px-3 py-2 text-left text-[11px] font-black uppercase text-slate-600 border-b border-slate-200 whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.spec_matrix.rows.map((row, i) => (
                            <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                              {row.map((c, j) => (
                                <td key={j} className="px-3 py-2 text-slate-800 border-b border-slate-100 whitespace-nowrap">
                                  {c || <span className="text-slate-300">—</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : Object.keys(data.specs || {}).length > 0 ? (
                    <table className="w-full text-sm">
                      <tbody>
                        {Object.entries(data.specs).map(([k, v]) => (
                          <tr key={k} className="border-b last:border-0">
                            <td className="py-2 pr-4 text-xs font-black text-slate-500 uppercase w-1/3 align-top">{k}</td>
                            <td className="py-2 text-slate-800">{v}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-8">Detailed spec matrix on the PDF spec sheet.</p>
                  )}
                </div>
              )}
              {tab === "downloads" && (
                <div>
                  {data.pdfs?.length > 0 ? (
                    <ul className="space-y-2">
                      {data.pdfs.map((d, i) => (
                        <li key={i}>
                          <a
                            href={`${BACKEND_URL}${d.url}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`furniture-specs-download-${i}`}
                            className="flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50/40 hover:border-emerald-500 group"
                          >
                            <div className="w-11 h-14 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-[10px] font-black shrink-0">PDF</div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-slate-800 group-hover:text-blue-700 truncate">{d.label}</p>
                              <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Hosted on sterilemate.com · {d.size_kb} KB
                              </p>
                            </div>
                            <svg className="w-4 h-4 text-slate-400 group-hover:text-blue-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15V3m0 12l-4-4m4 4l4-4M4 21h16" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </a>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-500">No spec sheet on file yet.</p>
                  )}
                </div>
              )}
            </div>
            <footer className="px-6 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={onClose} data-testid="furniture-specs-cancel" className="px-4 py-2 rounded-md text-sm font-bold text-slate-600 hover:bg-slate-100">Close</button>
              <button onClick={onOpenQuote} data-testid="furniture-specs-quote" className="px-4 py-2 rounded-md text-sm font-black bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" /> Request a Quote
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

function FurnitureQuoteModal({ product, onClose }) {
  const [form, setForm] = useState({
    name: "", email: "", organization: "", phone: "",
    quantity: 1, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        product_id: product.id,
        product_name: product.name,
        category: "healthcare-examination-furniture",
        ...form,
      });
      setDone(true);
      toast.success("Quote request sent — we'll reply within 1 business day");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send quote request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose} data-testid="furniture-quote-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between px-6 py-4 border-b">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">Request a Quote</p>
            <h3 className="text-xl font-black text-slate-900 mt-0.5">{product.name}</h3>
          </div>
          <button onClick={onClose} data-testid="furniture-quote-close" className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-2xl text-slate-500">×</button>
        </header>
        <div className="p-6">
          <div className="flex gap-4 mb-4 pb-4 border-b border-slate-100">
            <img src={`${BACKEND}${product.image}`} alt="" className="w-24 h-24 object-contain bg-slate-50 rounded border border-slate-200" />
            <div>
              <p className="text-xs text-slate-500 uppercase font-black">Product</p>
              <p className="text-sm font-bold text-slate-900">{product.name}</p>
              <p className="text-xs text-slate-500 mt-1">{product.tagline}</p>
            </div>
          </div>
          {done ? (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl mb-3">✓</div>
              <p className="font-black text-slate-900">Thanks — quote request received.</p>
              <p className="text-sm text-slate-600 mt-1">Our team replies within one business day.</p>
              <button onClick={onClose} className="mt-4 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-bold">Close</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Field label="Your Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="furniture-quote-name" />
              <Field label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="furniture-quote-email" />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="furniture-quote-org" />
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="furniture-quote-phone" />
              </div>
              <Field label="Quantity" type="number" min="1" value={form.quantity} onChange={(v) => setForm({ ...form, quantity: Number(v) || 1 })} testid="furniture-quote-qty" />
              <div>
                <label className="block text-xs font-black uppercase text-slate-500 mb-1">Notes / Upholstery / Configuration</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  data-testid="furniture-quote-notes"
                  placeholder="Preferred color, options, target install date…"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="furniture-quote-submit"
                className="w-full py-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-black"
              >{busy ? "Sending…" : "Send Quote Request"}</button>
              <p className="text-[11px] text-slate-500 text-center">
                We reply within one business day · Financing &amp; GSA pricing available.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, min, testid }) {
  return (
    <div>
      <label className="block text-xs font-black uppercase text-slate-500 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        min={min}
        required={required}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:border-blue-500"
      />
    </div>
  );
}
